import SoftPhoneModule from '@modules/SoftPhoneModule';
import React, { useMemo } from 'react';
import { useOutboundCallModalContext } from '@app/layouts/MainLayout/components/OutboundCall';
import { TransferringCallContext } from './TransferringCallContext';
import ToastModule from '@modules/ToastModule';
import Task from '@modules/TasksModule/Task';
import TasksModule from '@modules/TasksModule';
import useService from '@modules/ServiceModule/useService';
import TransfersModule from '@modules/TransfersModule';
import { FeatureFlagKeys, useFeatureFlags } from '@modules/FeatureFlagModule';
import { OutboundCallEntity } from '@lib/types/common-call-types';
import usePermissions from '@app/permissions/usePermissions';
import { permissions } from '@app/permissions/permissions';

export const TransferringCallProvider: React.FC<{
  children: React.ReactNode;
  task: Task | undefined;
  onClose: () => void;
}> = ({ children, task, onClose }) => {
  const { callToDetails } = useOutboundCallModalContext();
  const { userById } = useService();
  const {
    [FeatureFlagKeys.featureInboxCallWarmTransfers]:
      featureInboxCallWarmTransfers,
    [FeatureFlagKeys.featureInboxCallTransferToQueue]:
      featureInboxCallTransferToQueue,
    [FeatureFlagKeys.featureCallTransferToTechnicians]:
      featureCallTransferToTechnicians,
  } = useFeatureFlags();

  const hasTransferToTechniciansPermission = usePermissions(
    [permissions.general.ACTION_CALL_OPTIONS.CALL_TRANSFER_TO_TECHNICIANS.VIEW],
    'all',
  );
  const canTransferToTechnicians =
    featureCallTransferToTechnicians && hasTransferToTechniciansPermission;

  const value = useMemo(() => {
    const handleTransfer = (number: string) => {
      if (!callToDetails.number) {
        console.error('No call details number found', callToDetails);
        ToastModule.showToast({
          title: 'Failed to Transfer a call',
          status: 'critical',
          duration: 5000,
        });
      } else if (
        featureInboxCallWarmTransfers ||
        featureInboxCallTransferToQueue ||
        canTransferToTechnicians
      ) {
        TransfersModule.makeColdCallTransfer({
          taskOrCallId: task?.id ?? '',
          transferToNumber: number,
          agentId: callToDetails.agentId,
          transferType: callToDetails.entity,
          ...(canTransferToTechnicians &&
          callToDetails.entity === OutboundCallEntity.Technicians
            ? {
                tenantId: callToDetails.tenantId,
                technicianId: callToDetails.technicianId,
                technicianName: callToDetails.callDisplayName,
              }
            : {}),
          ...(featureInboxCallTransferToQueue &&
          callToDetails.entity === OutboundCallEntity.Queue
            ? {
                queueId: callToDetails.queueId,
                queueName: callToDetails.callDisplayName,
              }
            : {}),
        });
      } else {
        // determine if transfer to agent or number
        const hasAgent = !!callToDetails.agentId;

        // we need agent contactcenter user id to fetch transfer status if agent is present
        const agentContactCenterId = hasAgent
          ? userById[callToDetails.agentId!]?.contactCenterUserId
          : '';

        if (hasAgent) {
          // set transfer status for internal
          TasksModule.setTaskAttribute(task?.id ?? '', 'transferStatus', {
            inProgress: true,
            agentId: agentContactCenterId,
            initiatedAt: new Date(),
          });
          TasksModule.actionTask(task?.id ?? '', 'transfer');
        } else {
          // show toast and wrap up for external
          ToastModule.showToast({
            title: 'Transfer completed',
            status: 'success',
            duration: 5000,
          });
        }

        // transfer call
        SoftPhoneModule.transferNumber(number);
      }
    };

    const handleWarmTransfer = () => {
      if (!callToDetails.number) {
        console.error('No call details number found', callToDetails);
        ToastModule.showToast({
          title: 'Failed to Transfer a call',
          status: 'critical',
          duration: 5000,
        });
      } else {
        TransfersModule.startWarmCallTransfer(
          task?.id ?? '',
          callToDetails.number,
          callToDetails.entity,
          callToDetails.agentId,
          callToDetails.callDisplayName,
          canTransferToTechnicians &&
            callToDetails.entity === OutboundCallEntity.Technicians
            ? callToDetails.technicianId
            : undefined,
          canTransferToTechnicians &&
            callToDetails.entity === OutboundCallEntity.Technicians
            ? callToDetails.callDisplayName
            : undefined,
        );
        onClose(); // Close the modal after starting warm transfer
      }
    };

    return {
      from: {
        name: task?.displayName ?? '',
        number: task?.from ?? '',
      },
      to: {
        name: callToDetails?.callDisplayName,
        number: callToDetails?.number,
      },
      transferType: callToDetails.entity,
      handleTransfer,
      handleWarmTransfer,
      onClose,
    };
  }, [
    // display name can change after customer data is found
    task?.displayName,
    task?.from,
    task?.id,
    userById,
    callToDetails,
    featureInboxCallWarmTransfers,
    featureInboxCallTransferToQueue,
    canTransferToTechnicians,
    onClose,
  ]);

  return (
    <TransferringCallContext.Provider value={value}>
      {children}
    </TransferringCallContext.Provider>
  );
};
